import React, { useCallback, useEffect, useState, lazy } from 'react';
import { Facebook } from 'lucide-react';
const BACKGROUND_IMAGES = ['https://cdn.magicpatterns.com/uploads/fssYpKf8swLzdNNaRcNwxG/sj-website-home-banner-1.png', 'https://cdn.magicpatterns.com/uploads/3idp1uY2N511JBaJj5HAAE/sj-website-home-banner-2.png', 'https://cdn.magicpatterns.com/uploads/hWKiPjjFohRqkseJ6Qufg5/sj-website-home-banner-3.png', 'https://cdn.magicpatterns.com/uploads/vMP5ojfesyNXc5k5Zm6z65/sj-website-home-banner-4.png', 'https://cdn.magicpatterns.com/uploads/jrwLvEej2KvpzukSqKcwMX/sj-website-home-banner-5.png'];
// Alternate "Shine!" colour per slide
const SHINE_COLORS = ['#e91e8c', '#1a2744', '#e91e8c', '#1a2744', '#e91e8c'];
export function HeroBanner() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  // Preload the next image before transitioning
  const preloadImage = useCallback((index: number) => {
    if (!loadedImages.has(index)) {
      const img = new Image();
      img.src = BACKGROUND_IMAGES[index];
      img.onload = () => {
        setLoadedImages(prev => new Set(prev).add(index));
      };
    }
  }, [loadedImages]);
  useEffect(() => {
    // Preload the next image ahead of time
    const nextIndex = (currentImageIndex + 1) % BACKGROUND_IMAGES.length;
    preloadImage(nextIndex);
    const timer = setInterval(() => {
      setCurrentImageIndex(prev => {
        const next = (prev + 1) % BACKGROUND_IMAGES.length;
        return next;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [currentImageIndex, preloadImage]);
  const shineColor = SHINE_COLORS[currentImageIndex] || '#e91e8c';
  // Only render current slide and adjacent slides (prev/next) to reduce DOM and network load
  const visibleIndices = new Set([currentImageIndex, (currentImageIndex + 1) % BACKGROUND_IMAGES.length, (currentImageIndex - 1 + BACKGROUND_IMAGES.length) % BACKGROUND_IMAGES.length]);
  return <div className="relative w-full h-screen min-h-[600px] overflow-hidden bg-gray-100 flex flex-col" data-id="element-139">
      {/* Background Slideshow — only render current + adjacent slides */}
      {BACKGROUND_IMAGES.map((img, idx) => {
      if (!visibleIndices.has(idx)) return null;
      return <div key={idx} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${idx === currentImageIndex ? 'opacity-100' : 'opacity-0'}`} data-id="element-140">
            <img src={img} alt={`Smith and Jengs professional cleaning service banner ${idx + 1}`} className="w-full h-full object-cover object-center" loading={idx === 0 ? 'eager' : 'lazy'} fetchPriority={idx === 0 ? 'high' : undefined} width={1920} height={1080} data-id="element-141" />
            <div className="absolute inset-0 bg-white/10 mix-blend-overlay pointer-events-none" data-id="element-142" />
          </div>;
    })}

      {/* Content Overlay */}
      <div className="relative z-10 w-full h-full flex flex-col justify-between px-6 pt-32 pb-8 md:px-12 md:pt-36 md:pb-10" data-id="element-143">
        {/* Main Content */}
        <main className="flex-1 flex flex-col justify-center max-w-2xl mt-8 md:mt-0" data-id="element-144">
          <h1 className="text-[3.5rem] leading-[0.85] md:text-7xl lg:text-[5.5rem] md:leading-[0.8] font-normal text-[#1a2744] font-bebas tracking-wide uppercase" data-id="element-145">
            We Shine,
            <br data-id="element-146" />
            When You{' '}
            <span className="font-serif-playfair italic font-bold normal-case text-[3.5rem] md:text-[5rem] lg:text-[5.5rem] leading-none align-baseline inline-block transition-colors duration-1000" style={{
            color: shineColor
          }} data-id="element-147">
              Shine!
            </span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-[#1a2744] max-w-md font-medium" data-id="element-148">
            Residential and Commercial
            <br data-id="element-149" />
            Cleaning Experts
          </p>

          <div className="mt-8" data-id="element-150">
            <button className="px-6 py-2 rounded-full border border-[#e91e8c] text-[#e91e8c] bg-transparent font-semibold flex items-center gap-3 transition-all duration-300 text-sm tracking-wider uppercase" data-id="element-151">
              Book Online
              <span data-id="element-152">&rarr;</span>
            </button>
          </div>
        </main>

        {/* Bottom Section: Large Text & Social Icon */}
        <div className="w-full relative flex justify-center items-end mt-auto pt-10" data-id="element-153">
          <img src="https://cdn.magicpatterns.com/uploads/sUut1eTBvUUEHm5bBaK9Yr/sj-website-sj-text-header.svg" alt="Smith & Jengs" className="w-[80vw] max-w-[1200px] h-auto select-none" width={1200} height={120} data-id="element-154" />

          {/* Facebook Icon */}
          <div className="absolute right-0 bottom-12 md:bottom-16" data-id="element-155">
            <a href="#" className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center text-[#1a2744] hover:bg-white hover:text-[#e91e8c] transition-colors shadow-sm" aria-label="Visit Smith and Jengs on Facebook" data-id="element-156">
              <Facebook className="w-4 h-4 fill-current" data-id="element-157" />
            </a>
          </div>
        </div>
      </div>
    </div>;
}